import { useState } from 'react';

import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { resolveLogoText, resolveTextSize } from '../render/text';
import type { LogoTemplateProps } from '../types';
import { useLogoTemplateReady } from '../use-logo-template-ready';

const TEXT_SIZING = { maxWidth: 210, baseSize: 50, minSize: 22, charWidth: 0.45 };
const ICON_WIDTH = 68;
const ICON_GAP = 4;
const CONTENT_HEIGHT = 100;
const VIEWBOX_PADDING = 6;
const ICON_VIEWBOX_WIDTH = 146;
const ICON_VIEWBOX_HEIGHT = 132;
const ICON_VISUAL_CENTER_Y = 68;
const VERTICAL_OFFSET = 3;
const MEASURE_FONT_SIZE = TEXT_SIZING.baseSize;

const ICON_PATH =
  'M79.14,85.24c0,7.15,0,20.01,0,32.51l-15.7-7.28v-40.91h46v91.7c-5.44-2.35-10.67-4.78-15.69-7.28-.01-49.35-.02-56.73-.01-68.73h-14.59ZM52.81,7.44v18.13h65.43V7.4c-31.92,0-65.43.02-65.43.04ZM118.24,38.57H27.46c-15.17,0-27.46,12.3-27.46,27.47s12.3,27.46,27.46,27.46c13.98,0,25.52-10.44,27.24-23.96h-16.36c-1.48,4.6-5.8,7.92-10.88,7.92-6.31,0-11.43-5.12-11.43-11.43s5.12-11.43,11.43-11.43h90.78v-16.03Z';

export function HorizontalColumnName({
  text,
  palette,
  className,
  style,
  onReady,
}: LogoTemplateProps) {
  const logoText = resolveLogoText(text);
  const layoutKey = `${logoText}|${palette.headerLinks}`;
  const { fontsReady, setSvgRef, svgRef, signalReady } = useLogoTemplateReady({
    onReady,
    layoutKey,
  });

  const initialFontSize = resolveTextSize(logoText, TEXT_SIZING);
  const [layout, setLayout] = useState(() =>
    layoutFromMeasuredWidth(estimateWidth(logoText, MEASURE_FONT_SIZE), initialFontSize),
  );

  useIsomorphicLayoutEffect(() => {
    if (!onReady) return;
    if (!fontsReady || !svgRef.current) return;
    const measure = svgRef.current.querySelector('[data-logo-measure]') as SVGTextElement | null;
    if (!measure) return;

    const measuredWidth = measure.getBBox().width;
    const fontSize = resolveTextSize(logoText, TEXT_SIZING);
    const next = layoutFromMeasuredWidth(measuredWidth, fontSize);
    if (!layoutEqual(layout, next)) {
      setLayout(next);
      return;
    }

    signalReady(svgRef.current);
  }, [onReady, fontsReady, logoText, layout, signalReady, svgRef]);

  return (
    <svg
      ref={setSvgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${layout.viewBoxWidth} ${layout.viewBoxHeight}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{ maxHeight: '100%', maxWidth: '100%', display: 'block', ...style }}
      aria-hidden="true"
    >
      <text
        data-logo-measure=""
        fontSize={MEASURE_FONT_SIZE}
        fontFamily="'Barlow Condensed', BarlowCondensed-Regular, Inter, system-ui, sans-serif"
        visibility="hidden"
        x={0}
        y={-1000}
      >
        {logoText}
      </text>
      <g transform={`translate(${layout.offsetX}, ${VERTICAL_OFFSET})`}>
        <svg
          x={0}
          y={0}
          width={ICON_WIDTH}
          height={CONTENT_HEIGHT}
          viewBox={`0 0 ${ICON_VIEWBOX_WIDTH} ${ICON_VIEWBOX_HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <path d={ICON_PATH} fill={palette.headerLinks} fillRule="evenodd" />
        </svg>
        <text
          x={layout.textX}
          y={layout.textY}
          textAnchor="start"
          dominantBaseline="central"
          fill={palette.headerLinks}
          fontFamily="'Barlow Condensed', BarlowCondensed-Regular, Inter, system-ui, sans-serif"
          fontSize={layout.fontSize}
        >
          {logoText}
        </text>
      </g>
    </svg>
  );
}

type Layout = {
  fontSize: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
  offsetX: number;
  textX: number;
  textY: number;
};

function layoutFromMeasuredWidth(measuredAtBase: number, fontSize: number): Layout {
  const textWidth = measuredAtBase * (fontSize / MEASURE_FONT_SIZE);
  const templateWidth = ICON_WIDTH + ICON_GAP + textWidth;
  const viewBoxWidth = templateWidth + VIEWBOX_PADDING;
  const viewBoxHeight = CONTENT_HEIGHT + VIEWBOX_PADDING;

  return {
    fontSize,
    viewBoxWidth,
    viewBoxHeight,
    offsetX: (viewBoxWidth - templateWidth) / 2,
    textX: ICON_WIDTH + ICON_GAP,
    textY: textYAlignedToIcon(fontSize),
  };
}

function textYAlignedToIcon(textSize: number): number {
  const scale = Math.min(ICON_WIDTH / ICON_VIEWBOX_WIDTH, CONTENT_HEIGHT / ICON_VIEWBOX_HEIGHT);
  const renderedHeight = ICON_VIEWBOX_HEIGHT * scale;
  const slotOffsetY = (CONTENT_HEIGHT - renderedHeight) / 2;
  const iconCenterY = slotOffsetY + ICON_VISUAL_CENTER_Y * scale;
  return iconCenterY + textSize * 0.05;
}

function estimateWidth(text: string, fontSize: number): number {
  return text.length * fontSize * TEXT_SIZING.charWidth;
}

function layoutEqual(a: Layout, b: Layout): boolean {
  return (
    Math.abs(a.fontSize - b.fontSize) < 0.01 &&
    Math.abs(a.viewBoxWidth - b.viewBoxWidth) < 0.01 &&
    Math.abs(a.textY - b.textY) < 0.01
  );
}
